import React from "react";
import PropTypes from "prop-types";
import { FaCheck, FaEdit, FaPlus, FaTimes, FaTrash } from "react-icons/fa";

function VideoLinksSection({
  title,
  description,
  inputValue,
  onInputChange,
  inputPlaceholder,
  onAdd,
  addButtonLabel,
  addButtonClassName,
  items,
  emptyText,
  editItemId,
  editItemUrl,
  onEditItemUrlChange,
  editPlaceholder,
  onSaveEdit,
  onCancelEdit,
  onStartEdit,
  onDelete,
}) {
  return (
    <div className="bg-white rounded-2xl shadow p-5 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">{title}</h3>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <input
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          placeholder={inputPlaceholder}
          className="border px-3 py-2 rounded-lg w-full md:flex-1"
        />
        <button
          type="button"
          className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white transition ${addButtonClassName}`}
          onClick={onAdd}
        >
          <FaPlus />
          {addButtonLabel}
        </button>
      </div>

      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-gray-400">
          {emptyText}
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((item) => (
            <li key={item.id} className="rounded-xl border border-gray-200 p-4">
              {editItemId === item.id ? (
                <div className="flex flex-col gap-3 md:flex-row md:items-center">
                  <input
                    value={editItemUrl}
                    onChange={(e) => onEditItemUrlChange(e.target.value)}
                    className="border px-3 py-2 rounded-lg w-full md:flex-1"
                    placeholder={editPlaceholder}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700"
                      onClick={onSaveEdit}
                    >
                      <FaCheck />
                      Save
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-50"
                      onClick={onCancelEdit}
                    >
                      <FaTimes />
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm font-medium text-sky-700 underline break-all"
                  >
                    {item.url}
                  </a>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700 transition hover:bg-amber-100"
                      onClick={() => onStartEdit(item)}
                    >
                      <FaEdit />
                      Edit
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 transition hover:bg-rose-100"
                      onClick={() => onDelete(item.id)}
                    >
                      <FaTrash />
                      Remove
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

VideoLinksSection.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  inputValue: PropTypes.string.isRequired,
  onInputChange: PropTypes.func.isRequired,
  inputPlaceholder: PropTypes.string.isRequired,
  onAdd: PropTypes.func.isRequired,
  addButtonLabel: PropTypes.string.isRequired,
  addButtonClassName: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      url: PropTypes.string,
    })
  ).isRequired,
  emptyText: PropTypes.string.isRequired,
  editItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  editItemUrl: PropTypes.string.isRequired,
  onEditItemUrlChange: PropTypes.func.isRequired,
  editPlaceholder: PropTypes.string.isRequired,
  onSaveEdit: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  onStartEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

VideoLinksSection.defaultProps = {
  editItemId: null,
};

export default VideoLinksSection;
